import { parseDomains } from "app/pages/sales-intelligence/sub-modules/management/store/effects/parsing";
import { fetchExcelQuotaThunk } from "app/pages/sales-intelligence/sub-modules/common/store/effects";
import type {
    ParseDomainsResult,
    CompanyParsingErrors,
} from "app/pages/sales-intelligence/sub-modules/management/components/types";
import type { CreateOpportunityListResponse } from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

import * as actions from "../actions/enrichment";
import type {
    EnrichedState,
    EnrichmentConfigItem,
    EnrichmentErrorState,
    EnrichmentPreview,
    PostCsvProps,
} from "../type";

export const postEnrichmentCsvThunk =
    ({ columns, country, domainFieldName, file }: PostCsvProps) =>
    async (
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        { si: { api } }: ThunkDependencies,
    ): Promise<EnrichedState | EnrichmentErrorState> => {
        try {
            dispatch(actions.postEnrichmentCsvAsync.request());

            const fData = new FormData();
            fData.append("file", file);
            fData.append("columns", JSON.stringify(columns));
            fData.append("country", JSON.stringify(country));
            fData.append("domainFieldName", domainFieldName);

            const response = await api.enrichment.postCsv(fData);

            if (!response.ok) {
                const error = await response.text();
                throw Error(error);
            }

            const result = await response.json();
            dispatch(actions.postEnrichmentCsvAsync.success(result));

            return result;
        } catch (error) {
            dispatch(actions.postEnrichmentCsvAsync.failure(error));
            return { error: error.message, id: null };
        }
    };

export const parseEnrichmentCsvThunk =
    (csvFile: File, quotaRemaining: number) =>
    async (dispatch: ThunkDispatchCommon): Promise<ParseDomainsResult | CompanyParsingErrors> => {
        try {
            dispatch(actions.parseEnrichmentCsvAsync.request());

            const parseResult = await parseDomains(csvFile, quotaRemaining, true, true);

            dispatch(actions.parseEnrichmentCsvAsync.success(parseResult));
            return parseResult;
        } catch (error) {
            dispatch(actions.parseEnrichmentCsvAsync.failure(error));
            return error;
        }
    };

export const getEnrichedCsvThunk =
    (id: string) =>
    async (
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        { si: { api } }: ThunkDependencies,
    ): Promise<string> => {
        try {
            dispatch(actions.getEnrichedCsvAsync.request());

            const result = await api.enrichment.getEnrichedCsv(id);

            dispatch(actions.getEnrichedCsvAsync.success(result));
            dispatch(fetchExcelQuotaThunk());

            return result;
        } catch (error) {
            dispatch(actions.getEnrichedCsvAsync.failure(error));
            return error;
        }
    };

export const getSelectedColumnsConfigThunk =
    () =>
    async (
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        { si: { api } }: ThunkDependencies,
    ): Promise<readonly EnrichmentConfigItem[]> => {
        try {
            dispatch(actions.getSelectedColumnsConfigAsync.request());

            const result = await api.enrichment.getSelectedColumnsConfig();
            dispatch(actions.getSelectedColumnsConfigAsync.success(result));

            return result;
        } catch (error) {
            dispatch(actions.getSelectedColumnsConfigAsync.failure(error));
            return error;
        }
    };

export const getPreviewThunk =
    (domains: string[]) =>
    async (
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        { si: { api } }: ThunkDependencies,
    ): Promise<EnrichmentPreview> => {
        try {
            dispatch(actions.getPreviewAsync.request());

            const result = await api.enrichment.getPreview(domains);

            dispatch(actions.getPreviewAsync.success(result));

            return result;
        } catch (error) {
            dispatch(actions.getPreviewAsync.failure(error));
            return error;
        }
    };

export const createStaticListThunk =
    (domains: string[], name: string, country: number) =>
    async (
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        deps: ThunkDependencies,
    ): Promise<CreateOpportunityListResponse> => {
        try {
            dispatch(actions.createStaticListAsync.request());

            const response = await deps.si.api.opportunities.createOpportunityList({
                name,
                domains,
                country,
            });

            dispatch(actions.createStaticListAsync.success());

            return response;
        } catch (error) {
            dispatch(actions.createStaticListAsync.failure(error));
            return error;
        }
    };
